import { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
} from '@monksflow/monks-ui';
import { CalendarIcon, ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ScheduleDaysField, ScheduleEnabledField } from './ScheduleFields';
import { useScheduleSummary, type ScheduleValue } from './scheduleValue';

export type ScheduleFormData = ScheduleValue;
export type { ScheduleValue };

interface ScheduleEditorProps {
  schedule?: ScheduleValue | null;
  onSave: (data: ScheduleFormData) => void;
  isSaving: boolean;
  /**
   * Allow submitting an untouched form. Used where saving the inherited values is itself
   * meaningful, e.g. creating a workspace override that starts out equal to the org schedule.
   */
  allowSaveWhenUnchanged?: boolean;
  /**
   * Block submitting regardless of the form state. The parent owns both the condition and the
   * explanation shown to the user, so nothing here says why.
   */
  disableSave?: boolean;
}

const EMPTY_SCHEDULE: ScheduleValue = { daysOfWeek: [], enabled: false };

/**
 * A run schedule as a two-item accordion with its own save button, for a page where the schedule
 * is the only thing being edited. Where a save covers more than the schedule, compose
 * `ScheduleEnabledField` and `ScheduleDaysField` directly instead.
 */
export const ScheduleEditor = ({
  schedule,
  onSave,
  isSaving,
  allowSaveWhenUnchanged = false,
  disableSave = false,
}: ScheduleEditorProps) => {
  const { t } = useTranslation('admin');

  const initial = schedule ?? EMPTY_SCHEDULE;

  // Edits are held against the value they were made on, so a refetch that changes the stored
  // schedule replaces what is shown rather than leaving a stale draft in place. The form this
  // replaced got the same effect from react-hook-form's `values` prop.
  const identity = `${initial.enabled}|${[...initial.daysOfWeek].sort().join()}`;
  const [draft, setDraft] = useState<{ identity: string; value: ScheduleValue } | null>(null);
  const value = draft?.identity === identity ? draft.value : initial;
  const setValue = (next: ScheduleValue) => setDraft({ identity, value: next });

  const summary = useScheduleSummary(value);

  const isDirty =
    value.enabled !== initial.enabled ||
    value.daysOfWeek.join() !== [...initial.daysOfWeek].sort().join();

  const dayCount = value.daysOfWeek.length;
  const daysSummary =
    dayCount === 0
      ? t('admin.scheduleEditor.noDaysSelected')
      : dayCount === 7
        ? t('admin.scheduleEditor.daily')
        : t('admin.scheduleEditor.dayPerWeek', { count: dayCount });

  return (
    <div className="flex flex-col gap-lg">
      <Accordion type="single" collapsible className="w-full">
        {/* eslint-disable-next-line i18next/no-literal-string -- accordion item id */}
        <AccordionItem value="enable">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-xs">
              <ClockIcon className="size-4 text-muted-foreground" />
              <span className="font-medium">{t('admin.scheduleEditor.status')}</span>
              <Badge variant={value.enabled ? 'positive' : 'secondary'} roundness="full">
                {value.enabled
                  ? t('admin.scheduleEditor.enabled')
                  : t('admin.scheduleEditor.disabled')}
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-md">
              <ScheduleEnabledField value={value} onChange={setValue} disabled={isSaving} />
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* eslint-disable-next-line i18next/no-literal-string -- accordion item id */}
        <AccordionItem value="schedule">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-xs">
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span className="font-medium">{t('admin.scheduleEditor.weeklySchedule')}</span>
              <span className="text-body-sm text-muted-foreground">{daysSummary}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-md">
              <ScheduleDaysField value={value} onChange={setValue} disabled={isSaving} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex items-center justify-between gap-md border-t pt-md">
        <p className="text-body-sm text-muted-foreground">{summary}</p>
        <Button
          onClick={() => onSave(value)}
          disabled={disableSave || isSaving || (!allowSaveWhenUnchanged && !isDirty)}
        >
          {isSaving ? t('admin.scheduleEditor.saving') : t('admin.scheduleEditor.saveSchedule')}
        </Button>
      </div>
    </div>
  );
};
